import { $t } from '@/i18n';
import backendAPI from '@/modules/ai-page/backendAPI';
import { getuuid } from '@/utils/id';
import { MessageType } from './message';
import { fetchReachableRepoObjects } from './repoObjectLoader';
/**
 * 通用 Worker 任务执行器
 * 封装 Worker 的创建、消息监听、错误处理和清理，消除重复模板代码。
 * @param message 发送给 Worker 的消息
 * @param options.id 可选的消息 ID，用于匹配响应（不传则不做 id 匹配）
 * @returns Worker 返回的 event.data
 */
async function runWorkerTask(message, options) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const FileWorker = await import('./git.worker').then((module) => module.default);
    const worker = new FileWorker();
    return new Promise((resolve, reject) => {
        const cleanup = () => {
            worker.removeEventListener('message', messageHandler);
            worker.removeEventListener('error', errorHandler);
            worker.terminate();
        };
        const errorHandler = (error) => {
            console.error($t('k_c8b3e950'), error);
            cleanup();
            reject(error);
        };
        const messageHandler = (event) => {
            const eventData = event.data || {};
            // 如果指定了 id，只处理匹配的响应
            if (options?.id && eventData.id !== options.id)
                return;
            cleanup();
            if (eventData.type === 'error') {
                console.error($t('k_d97d79e'), eventData.error);
                reject(new Error(eventData.error));
                return;
            }
            resolve(eventData);
        };
        worker.addEventListener('message', messageHandler);
        worker.addEventListener('error', errorHandler);
        worker.postMessage(message);
    });
}
const commitInfoCache = new Map();
export async function getAIPageCodeByCommitId(originCommitId, compareCommitId) {
    const objects = await fetchReachableRepoObjects([originCommitId, compareCommitId], oids => backendAPI.repositoryAPI.readRepoObjects(oids));
    const result = await runWorkerTask({
        type: MessageType.GET_CODE,
        objects,
        originCommitId,
        compareCommitId,
    });
    return {
        originFiles: result.originFiles,
        compareFiles: result.compareFiles,
    };
}
export async function getAIPageCommitInfo(commitId) {
    if (!commitId)
        return null;
    if (!commitInfoCache.has(commitId)) {
        const task = (async () => {
            const objects = await backendAPI.repositoryAPI.readRepoObjects([commitId]).catch(() => ({}));
            const commitObject = objects?.[commitId];
            if (!commitObject)
                return null;
            const id = getuuid();
            const result = await runWorkerTask({
                type: MessageType.GET_COMMIT_INFO,
                objects: { [commitId]: commitObject },
                commitId,
                id,
            }, { id });
            return (result.data || null);
        })();
        commitInfoCache.set(commitId, task);
    }
    const task = commitInfoCache.get(commitId);
    try {
        const result = await task;
        // 避免缓存失败结果，后续调用仍可重试
        if (!result) {
            commitInfoCache.delete(commitId);
        }
        return result;
    }
    catch (error) {
        commitInfoCache.delete(commitId);
        return null;
    }
}
export async function getSource() {
    const projectInfo = await backendAPI.projectAPI.getProjectInfo();
    if (!projectInfo) {
        return {};
    }
    const objects = await fetchReachableRepoObjects([projectInfo.mainBranch], oids => backendAPI.repositoryAPI.readRepoObjects(oids)).catch((error) => {
        console.log('getObjects error', error);
        return {};
    });
    const id = getuuid();
    const result = await runWorkerTask({
        type: MessageType.GET_SOURCE,
        objects,
        commitId: projectInfo.mainBranch,
        id,
    }, { id });
    return result.data;
}
